/**
 * Service prices — the shapes shared by every view that lists a pro's `seller-service-price` rows
 * (the public profile catalogue and the pro's own settings).
 *
 * Price is a two-axis model — `priceType` (how the amount reads) × `priceUnit` (what it is
 * charged per). Field names are camelCase because v3 JSON:API attributes arrive camelCase already
 * (see .claude/rules/api-v3-jsonapi.md). The raw `amount` / `amountMax` arrive as decimal strings
 * and are parsed to numbers by the mapper.
 */

/** Depth of a service within its category. `sub2` rows render under a `sub1`. */
export type ServiceLevel = 'sub1' | 'sub2';

/** How a price amount reads. Mirrors the BE `priceType` attribute verbatim. */
export enum PriceType {
  FROM = 'from',
  FIXED = 'fixed',
  RANGE = 'range'
}

/**
 * What a price is charged per. Mirrors the BE `priceUnit` attribute verbatim. `job` is a
 * flat price (no `/ unit` suffix); the rest render a suffix (`/ τ.μ.`, `/ τεμ.`, `/ ώρα`).
 */
export enum PriceUnit {
  JOB = 'job',
  SQM = 'sqm',
  ITEM = 'item',
  HOUR = 'hour'
}

/** Price payload for a single service. Absent when a service exists but has no price. */
export interface ServicePrice {
  priceType: PriceType;
  priceUnit: PriceUnit;
  /** Ex-VAT amount. The single value for every type except `range`, where it is the lower bound. */
  amount: number;
  /** Upper bound of a `range` price. Ignored for every other type. */
  amountMax?: number;
}

/* --------------------------------------------------------------------------
 * Raw JSON:API resource shapes. A row carries price-only attributes and links
 * its labels by relationship (`category` → `categories`, `sub1Service`/`sub2Detail`
 * → `choice-value` / `custom-service` / `custom-detail`), with the label resources
 * sideloaded in `included`. These feed the mappers only; UI components consume
 * the display shapes.
 * ------------------------------------------------------------------------ */

/** A JSON:API resource linkage; `data` is `null` when the relationship is empty. */
export interface ServicePriceRelationship {
  data: { type: string; id: string } | null;
}

/**
 * The relationships that place a row. A relationship's `data` is `null` when the row prices a
 * level shallower than that label (whole category, or no detail).
 */
export interface ServicePriceRowRelationships {
  category?: ServicePriceRelationship;
  sub1Service?: ServicePriceRelationship;
  sub2Detail?: ServicePriceRelationship;
}

/**
 * Price attributes of a `seller-service-price` row. The labels are not here — they arrive
 * via the relationships. `amount`/`amountMax` arrive as decimal strings.
 */
export interface SellerServicePriceAttributes {
  priceType: PriceType;
  priceUnit: PriceUnit;
  amount: string;
  /** Upper bound of a `range` price; `null` (or absent) for every other type. */
  amountMax?: string | null;
  /**
   * Info-modal copy for the node this row prices (#5613): sanitized, already-localized
   * rich-text HTML. `null` when ops wrote nothing — which is what hides the ⓘ, so an
   * empty modal is never reachable.
   */
  includedExcluded?: string | null;
  termsAndConditions?: string | null;
}

/** A single `seller-service-price` row; each endpoint may add its own attributes. */
export interface SellerServicePriceResource<TAttributes = SellerServicePriceAttributes> {
  id: string;
  type: string;
  attributes: TAttributes;
  relationships?: ServicePriceRowRelationships;
}

/**
 * A sideloaded label resource (`categories`, `choice-value`, `custom-service`, `custom-detail`).
 *
 * `title` is the display label. `questionId` is the job-form question id added by #5615 — present
 * on the sub1 service and sub2 detail records, and the only place this app reads it from. There is
 * no separate answer-id attribute: a `choice-value` record **is** the answer, so its own resource
 * `id` is the chosen option id. `questionId` is absent for a record the form cannot pre-answer
 * (e.g., a custom service the pro typed in), which is a valid payload: the click then degrades to
 * a coarser skip.
 */
export interface ServicePriceIncludedResource {
  id: string;
  type: string;
  attributes: {
    title: string;
    questionId?: number | string | null;
    /**
     * The category's single combined info field (#5613), on the sideloaded `categories`
     * record only. `null` when unset. Absent on the sub1/sub2 label records, whose info
     * copy rides on the price row's own attributes instead.
     */
    servicePriceInfo?: string | null;
  };
}
